import {
  always,
  both,
  complement,
  cond,
  has,
  ifElse,
  isEmpty,
  isNil,
  mapObjIndexed,
  pathOr,
  pipe,
  prop,
  propOr,
  replace,
  split,
  T,
} from 'ramda';

import { isObject } from 'utils/helpers';

/**
 * Equals literal true
 *
 * Equality comparison for true string that does no conversion
 *
 * @param {string} value
 * @returns {boolean}
 */
const equalsLiteralTrue = value => value == 'true';

/**
 * Equals literal false
 *
 * Equality comparison for false string that does no conversion
 *
 * @param {string} value
 * @returns {boolean}
 */
const equalsLiteralFalse = value => value == 'false';

/**
 * Sanitize
 *
 * @param {*} value
 * @returns {*}
 */
const santitize = value =>
  cond([
    [isEmpty, always(undefined)],
    [equalsLiteralTrue, always(true)],
    [equalsLiteralFalse, always(false)],
    [both([!equalsLiteralTrue, T]), always(value)],
  ])(value);

/**
 * Santizie gate
 *
 * Function to fail fast on nil
 *
 * @param {*} value
 * @returns {*}
 */
const santitizeGate = value => {
  if (isNil(value)) return;
  return santitize(value);
};

/**
 * Sanitize values
 *
 * Sanitizes form value so they will be accepted
 * by the middlewares and backend
 *
 * @param {object}
 * @returns {object}
 */
export const sanitizeValues = mapObjIndexed(santitizeGate);

/**
 * Get server error
 *
 * Extract the required error from response
 * errors object
 *
 * @param {object} errors
 * @param {string} error
 */
export const getServerError = (errors, error) => {
  if (!error || isEmpty(error)) return;

  const errorData = propOr(pathOr(prop('default', errors), ['data', 'message'], error), prop('status', error), errors);
  return error && !isEmpty(error) && errorData;
};

/**
 * Extracts defaults from form config
 *
 * @param {string} key
 * @returns {Function}
 */
const fieldConfigExtractor = key => {
  /**
   * Check if has nested keys
   *
   * @param {string}
   * @returns {boolean}
   */
  const hasNested = both(isObject, complement(has(key)));

  /**
   * Returns property of data
   *
   * @param {object}
   * @returns {*}
   */
  const extractDefault = prop(key);

  /**
   * Extract defaults
   *
   * Checks if defaults have nested defaults and returns
   * Recurse if nested e.g.
   * ```js
   * {
   *   foo: {
   *     default: ''
   *   },
   *   bar: {
   *     boo: {
   *       default: ''
   *     }
   *   }
   * }
   * ```
   *
   * @param {object}
   * @returns {*}
   */
  const extractDefaults = ifElse(hasNested, field => getProperties(field), extractDefault);

  /**
   * Get properties
   *
   * Extracts defaults for all field properties
   *
   * @param {object}
   * @returns {object}
   */
  const getProperties = mapObjIndexed(extractDefaults);

  return getProperties;
};

/**
 * Extract field defaults
 *
 * @param {object}
 * @returns {object}
 */
export const extractFieldDefaults = fieldConfigExtractor('default');

/**
 * Get form path
 *
 * Allows us to get string form path as array, e.g.
 *
 * ```js
 * getFormPath('values[foo][bar]') // ['values', 'foo', 'bar']
 * ```
 *
 * @param {string}
 * @returns {array}
 */
export const getFormPath = pipe(replace(']', ''), split('['));

export const formPropsWithTrimOnBlur = formProps => ({
  ...formProps,
  handleBlur: event => {
    if (event.target.name && event.target.value) {
      formProps.setFieldValue(event.target.name, event.target.value.trim());
    }

    formProps.handleBlur(event);
  },
});
